/* global Office */

/**
 * officeFileHelper — reads the active MS Office document (Word, Excel, or PowerPoint)
 * as raw ArrayBuffer bytes in chunks using the Office.js API.
 */
export function getActiveDocumentBytes(): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    try {
      if (typeof Office === 'undefined' || !Office.context?.document) {
        reject(
          new Error(
            'Office.js is not initialized or not running inside an Office application host.',
          ),
        );
        return;
      }

      // getFileAsync reads the entire active document in a compressed (zipped) format
      // which corresponds to a standard .docx, .xlsx, or .pptx file.
      Office.context.document.getFileAsync(
        Office.FileType.Compressed,
        { sliceSize: 65_536 }, // Read in 64KB chunks
        (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            reject(new Error(`Failed to read document file: ${result.error.message}`));
            return;
          }

          const file = result.value;
          const sliceCount = file.sliceCount;
          const slices: Uint8Array[] = [];
          let slicesReceived = 0;

          // Recursive helper to read slices sequentially
          function getSlice(index: number) {
            file.getSliceAsync(index, (sliceResult) => {
              if (sliceResult.status === Office.AsyncResultStatus.Failed) {
                file.closeAsync();
                reject(
                  new Error(`Failed to read document slice ${index}: ${sliceResult.error.message}`),
                );
                return;
              }

              // Save chunk bytes
              slices.push(new Uint8Array(sliceResult.value.data));
              slicesReceived++;

              if (slicesReceived === sliceCount) {
                // All chunks received. Close the file and combine them.
                file.closeAsync(() => {
                  const totalLength = slices.reduce((sum, slice) => sum + slice.length, 0);
                  const combined = new Uint8Array(totalLength);
                  let offset = 0;
                  for (const slice of slices) {
                    combined.set(slice, offset);
                    offset += slice.length;
                  }
                  resolve(combined.buffer);
                });
              } else {
                getSlice(slicesReceived);
              }
            });
          }

          getSlice(0);
        },
      );
    } catch (error) {
      reject(error);
    }
  });
}
